'use client';
import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ChevronDown, Check } from 'lucide-react';
import { SUPPORTED_LANGUAGES, Language } from '@/lib/i18n';
import { useLanguage } from '@/context/LanguageContext';

interface LanguageSelectorProps {
  compact?: boolean;
}

export default function LanguageSelector({ compact = false }: LanguageSelectorProps) {
  const { language, setLanguage, t } = useLanguage();
  const [open, setOpen] = useState(false);
  const [dropdownStyle, setDropdownStyle] = useState<React.CSSProperties>({});
  const [mounted, setMounted] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const current = SUPPORTED_LANGUAGES.find((l) => l.code === language) ?? SUPPORTED_LANGUAGES[0];

  useEffect(() => {
    setMounted(true);
  }, []);

  useEffect(() => {
    function handleClickOutside(e: MouseEvent) {
      if (buttonRef.current && !buttonRef.current.contains(e.target as Node)) {
        const target = e.target as Element;
        if (!target.closest('[data-language-dropdown]')) {
          setOpen(false);
        }
      }
    }
    if (open) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  const handleToggle = () => {
    if (!open && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      const dropdownHeight = 200;
      const dropdownWidth = 220;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceAbove = rect.top;

      let top: number;
      let left: number;

      if (spaceBelow < dropdownHeight && spaceAbove > spaceBelow) {
        top = rect.top - Math.min(dropdownHeight, spaceAbove) - 8;
      } else {
        top = rect.bottom + 8;
      }

      left = rect.right - dropdownWidth;
      if (left < 8) left = 8;
      if (left + dropdownWidth > window.innerWidth - 8) {
        left = window.innerWidth - dropdownWidth - 8;
      }

      setDropdownStyle({
        position: 'fixed',
        top,
        left,
        width: dropdownWidth,
        zIndex: 999999,
      });
    }
    setOpen(prev => !prev);
  };

  const handleSelect = (code: Language) => {
    setLanguage(code);
    setOpen(false);
  };

  const dropdown = open && mounted ? createPortal(
    <div
      data-language-dropdown
      style={dropdownStyle}
      className="rounded-xl bg-card border border-border shadow-2xl overflow-hidden fade-in"
    >
      {/* Header */}
      <div className="px-3 py-2.5 border-b border-border">
        <p className="text-xs font-600 text-foreground">{t?.language?.selectLanguage || 'Select Language'}</p>
      </div>

      {/* Language list */}
      <div className="py-1" role="listbox" aria-label={t?.language?.selectLanguage || 'Select Language'}>
        {SUPPORTED_LANGUAGES.map((lang) => (
          <button
            key={lang.code}
            type="button"
            role="option"
            aria-selected={language === lang.code}
            onClick={() => handleSelect(lang.code)}
            className={`w-full flex items-center justify-between px-3 py-2.5 text-sm transition-colors hover:bg-white/5
              ${language === lang.code ? 'text-primary bg-primary/5' : 'text-muted-foreground hover:text-foreground'}
            `}
          >
            <span className="flex items-center gap-2.5">
              <span className="text-base">{lang.flag}</span>
              <span className="font-500">{lang.nativeName}</span>
            </span>
            {language === lang.code && <Check size={13} className="text-primary shrink-0" />}
          </button>
        ))}
      </div>
    </div>,
    document.body
  ) : null;

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleToggle}
        className="flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-white/5 transition-colors text-sm"
        aria-label={t?.language?.selectLanguage || 'Select Language'}
        aria-haspopup="listbox"
        aria-expanded={open}
        title={t?.language?.selectLanguage || 'Select Language'}
      >
        <span className="text-base leading-none">{current.flag}</span>
        {!compact && (
          <>
            <span className="hidden sm:block font-medium">{current.nativeName}</span>
            <ChevronDown size={12} className="text-muted-foreground" />
          </>
        )}
      </button>
      {dropdown}
    </>
  );
}
